import { startingRange } from '../range/starting';
/** Map a Position onto its spot-key bucket. EP = UTG/UTG1/UTG2, MP = LJ/HJ, then CO/BTN/SB verbatim.
 *  BB never opens or 3-bets-then-defends, so it is never bucketed in practice; it maps to SB defensively. */
export const positionBucket = (pos) => {
    switch (pos) {
        case 'UTG':
        case 'UTG1':
        case 'UTG2':
            return 'EP';
        case 'LJ':
        case 'HJ':
            return 'MP';
        case 'CO':
            return 'CO';
        case 'BTN':
            return 'BTN';
        case 'SB':
        case 'BB':
            return 'SB';
    }
};
const bb = (state) => state.spec.stakes.bb;
/** A preflop raise = an actual raise, or a preflop jam that is a raise (jam-to above the big blind). */
const isPreflopRaise = (state, a) => a.kind === 'raise' || (a.kind === 'jam' && (a.amount ?? 0) > bb(state));
/**
 * Read the villain's preflop line off the hand history and derive the prioritized spot-key candidates.
 * Pure: depends only on `state.history`, `state.players`, and `state.spec.stakes`.
 */
export const deriveVillainSpotKey = (state, villainSeat) => {
    const villain = state.players.find((p) => p.seat === villainSeat);
    if (!villain)
        throw new Error(`deriveVillainSpotKey: villain seat ${villainSeat} not in hand`);
    const villainPosition = villain.position;
    const preflop = state.history.filter((h) => h.street === 'preflop').map((h) => h.action);
    const raisers = preflop.filter((a) => isPreflopRaise(state, a)).map((a) => a.seat);
    const nRaises = raisers.length;
    const villainRaiseIdx = raisers.indexOf(villainSeat);
    const villainRaiseCount = raisers.filter((s) => s === villainSeat).length;
    const openerSeat = raisers[0];
    const openerPosition = openerSeat !== undefined ? state.players.find((p) => p.seat === openerSeat)?.position : undefined;
    const rfi = `${villainPosition}_RFI`;
    const WIDEST_FALLBACK = 'BTN_RFI'; // present in every shipped range set - the last-resort resolvable key.
    let role;
    let candidates;
    if (nRaises === 0) {
        role = 'limper';
        candidates = [rfi, WIDEST_FALLBACK];
    }
    else if (villainRaiseIdx === 0) {
        // villain made the first raise (the opener).
        if (nRaises === 1) {
            role = 'rfi';
            candidates = [rfi, WIDEST_FALLBACK];
        }
        else if (villainRaiseCount >= 2) {
            role = 'opener_v3b_4bettor';
            const b = positionBucket(villainPosition);
            candidates = [`${b}_open_v3b_4bet`, `${b}_open_v3b_call`, rfi, WIDEST_FALLBACK];
        }
        else {
            role = 'opener_v3b_caller';
            const b = positionBucket(villainPosition);
            candidates = [`${b}_open_v3b_call`, rfi, WIDEST_FALLBACK];
        }
    }
    else if (villainRaiseIdx >= 1) {
        // villain put in a later raise (3-bet, or a rarer cold-4-bet mapped to the same key family).
        role = 'threebettor';
        const b = openerPosition ? positionBucket(openerPosition) : 'BTN';
        candidates = [`${villainPosition}_vs_${b}_3bet`, rfi, WIDEST_FALLBACK];
    }
    else {
        // villain never raised but is voluntarily in: they called a raise.
        const b = openerPosition ? positionBucket(openerPosition) : 'BTN';
        if (nRaises === 1) {
            role = 'caller';
        }
        else {
            role = 'coldcaller';
        }
        candidates = [`${villainPosition}_vs_${b}_call`, rfi, WIDEST_FALLBACK];
    }
    return openerPosition !== undefined
        ? { role, villainPosition, openerPosition, candidates }
        : { role, villainPosition, candidates };
};
/**
 * Select the villain's starting WeightedRange for the live path. Derives the spot key from the villain's
 * preflop line + position, resolves the first candidate present in `rangeSet` via startingRange(), and
 * applies card removal for the board + hero's holes. Throws only if NONE of the candidates (including the
 * universal BTN_RFI fallback) exists in the set - i.e. a malformed range set.
 */
export const selectVillainStartingRange = (rangeSet, state, villainSeat, opts = {}) => {
    const read = deriveVillainSpotKey(state, villainSeat);
    for (const spotKey of read.candidates) {
        if (rangeSet.ranges[spotKey] !== undefined) {
            // arch is provenance-only on startingRange (unused in v1 - the tightness is already encoded by
            // WHICH range set the archetype uses), and we only have the id here, so it is intentionally omitted.
            const range = startingRange(rangeSet, spotKey, { dead: opts.dead });
            return { range, spotKey, role: read.role, villainPosition: read.villainPosition };
        }
    }
    throw new RangeError(`selectVillainStartingRange: none of [${read.candidates.join(', ')}] present in range set '${rangeSet.id}'`);
};
//# sourceMappingURL=villainRange.js.map